/**
 * Grid Class
 *
 * Represents a grid (hálózat) containing cells in the puzzle.
 * Migrated from the legacy Halozat constructor function in olaszmodra.js.
 */

import { Cell } from './Cell';
import { capitalize } from '../utils/string';

/**
 * Grid class representing a grid of cells in the puzzle.
 */
export class Grid {
    private _cells: Cell[] = [];
    private _minId = 0;
    private _maxId = 0;
    private _rows = 0;
    private _columns = 0;
    private _topMargin = 0;
    private _leftMargin = 0;
    private readonly _index: number;
    private readonly _elementId: string;

    constructor(index = 0) {
        this._index = index;
        this._elementId = `halozat-${index}`;
    }

    // ============================================================================
    // Cell Management
    // ============================================================================

    push(cell: Cell): void {
        this._cells.push(cell);
    }

    pop(): Cell | undefined {
        return this._cells.pop();
    }

    get(id: number): Cell {
        return this._cells[id - this._minId];
    }

    get cells(): Cell[] {
        return this._cells;
    }

    get length(): number {
        return this._cells.length;
    }

    // ============================================================================
    // Dimensions
    // ============================================================================

    get minId(): number {
        return this._minId;
    }

    set minId(value: number) {
        this._minId = value;
    }

    get maxId(): number {
        return this._maxId;
    }

    set maxId(value: number) {
        this._maxId = value;
        this._columns = (this._maxId - this._minId + 1) / this._rows;
    }

    get rows(): number {
        return this._rows;
    }

    set rows(value: number) {
        this._rows = value;
    }

    get columns(): number {
        return this._columns;
    }

    set columns(value: number) {
        this._columns = value;
    }

    get topMargin(): number {
        return this._topMargin;
    }

    get leftMargin(): number {
        return this._leftMargin;
    }

    get elementId(): string {
        return this._elementId;
    }

    get index(): number {
        return this._index;
    }

    // ============================================================================
    // Margin Initialization
    // ============================================================================

    initMargins(): void {
        // Calculate left margin (vbehuzas) - find first weak horizontal line
        let i = 0;
        while (
            i < this._cells.length &&
            !this._cells[i].hasWeakHorizontalLine
        ) {
            i++;
        }
        this._leftMargin =
            i === this._cells.length ? 0 : (i + 1) % this._columns;

        // Calculate top margin (fbehuzas) - find first weak vertical line
        i = 0;
        while (i < this._cells.length && !this._cells[i].hasWeakVerticalLine) {
            i++;
        }
        this._topMargin = i === this._cells.length ? 0 : i % this._columns;
        this._topMargin +=
            i > this._cells.length / this._rows && this._topMargin !== 0
                ? 1
                : 0;
    }

    // ============================================================================
    // Line Initialization
    // ============================================================================

    initLines(): void {
        // Disable lines at margins (rows)
        let i = 0;
        while (i < this._cells.length) {
            for (let j = 0; j < this._leftMargin; j++) {
                this._cells[i + j].disableHorizontalLine();
                this._cells[i + j].disableVerticalLine();
            }
            for (
                let j = this._columns - this._leftMargin - 1;
                j < this._columns;
                j++
            ) {
                if (j >= this._columns - this._leftMargin) {
                    this._cells[i + j].disableVerticalLine();
                }
                this._cells[i + j].disableHorizontalLine();
            }
            i += this._columns;
        }

        // Disable lines at margins (columns)
        for (i = 0; i < this._columns; i++) {
            for (let j = 0; j < this._topMargin; j += this._columns) {
                this._cells[i + j].disableVerticalLine();
                this._cells[i + j].disableHorizontalLine();
            }
            for (
                let j =
                    this._cells.length -
                    this._topMargin * this._columns -
                    this._columns;
                j < this._cells.length;
                j += this._columns
            ) {
                if (j >= this._cells.length - this._topMargin * this._columns) {
                    this._cells[i + j].disableHorizontalLine();
                }
                this._cells[i + j].disableVerticalLine();
            }
        }
    }

    // ============================================================================
    // Navigation
    // ============================================================================

    previousCell(id: number): number {
        if (id === this._minId) {
            return this._maxId;
        }
        return id - 1;
    }

    nextCell(id: number): number {
        if (id === this._maxId) {
            return this._minId;
        }
        return id + 1;
    }

    aboveCell(id: number): number {
        if (id === this._minId) {
            return this._maxId;
        }

        let newId = id - this._columns;
        if (newId < this._minId) {
            newId += this._rows * this._columns;
            newId--;
        }
        return newId;
    }

    belowCell(id: number): number {
        if (id === this._maxId) {
            return this._minId;
        }

        let newId = id + this._columns;
        if (newId > this._maxId) {
            newId -= this._rows * this._columns;
            newId++;
        }
        return newId;
    }

    // ============================================================================
    // Cell Selection
    // ============================================================================

    selectHorizontalCells(
        index: number,
        skipValidation = false,
        skipChannelCheck = false,
    ): number[] {
        const selected: number[] = [];
        const cellIndex = index - this._minId;
        const cell = this._cells[cellIndex];

        const isBridge = skipChannelCheck
            ? cell.isBridge
            : cell.isBridge && this.isHorizontalChannel(index);

        if (
            skipValidation ||
            (!cell.isEmpty && !cell.isBlack && !cell.isImage)
        ) {
            // Select cells to the right
            let i = cellIndex;
            const rowEnd =
                (Math.floor(cellIndex / this._columns) + 1) * this._columns - 1;
            while (
                i < rowEnd &&
                (!this._cells[i].hasHorizontalLine || isBridge) &&
                !this._cells[i + 1].isBlack &&
                !this._cells[i + 1].isImage &&
                !this._cells[i + 1].isEmpty &&
                (!isBridge || this._cells[i + 1].isBridge)
            ) {
                selected.push(i + 1 + this._minId);
                i++;
            }

            // Select cells to the left
            i = cellIndex - 1;
            const rowStart =
                Math.floor(cellIndex / this._columns) * this._columns;
            while (
                i >= rowStart &&
                (!this._cells[i].hasHorizontalLine || isBridge) &&
                !this._cells[i].isBlack &&
                !this._cells[i].isImage &&
                !this._cells[i].isEmpty &&
                (!isBridge || this._cells[i].isBridge)
            ) {
                selected.push(i + this._minId);
                i--;
            }
        }

        return selected;
    }

    selectVerticalCells(
        index: number,
        skipValidation = false,
        skipChannelCheck = false,
    ): number[] {
        const selected: number[] = [];
        const cellIndex = index - this._minId;
        const cell = this._cells[cellIndex];

        const isBridge = skipChannelCheck
            ? cell.isBridge
            : cell.isBridge && this.isVerticalChannel(index);

        if (
            skipValidation ||
            (!cell.isEmpty && !cell.isBlack && !cell.isImage)
        ) {
            // Select cells below
            let i = cellIndex;
            while (
                i + this._columns <= this._maxId - this._minId &&
                (!this._cells[i].hasVerticalLine || isBridge) &&
                !this._cells[i + this._columns].isBlack &&
                !this._cells[i + this._columns].isImage &&
                !this._cells[i + this._columns].isEmpty &&
                (!isBridge || this._cells[i + this._columns].isBridge)
            ) {
                selected.push(i + this._columns + this._minId);
                i += this._columns;
            }

            // Select cells above
            i = cellIndex - this._columns;
            while (
                i >= 0 &&
                (!this._cells[i].hasVerticalLine || isBridge) &&
                !this._cells[i].isBlack &&
                !this._cells[i].isImage &&
                !this._cells[i].isEmpty &&
                (!isBridge || this._cells[i].isBridge)
            ) {
                selected.push(i + this._minId);
                i -= this._columns;
            }
        }

        return selected;
    }

    // ============================================================================
    // Channel Detection
    // ============================================================================

    isHorizontalChannel(index: number): boolean {
        const selected = this.selectHorizontalCells(index, false, true);
        selected.push(index);

        const lines: number[] = [];
        for (const id of selected) {
            if (this._cells[id - this._minId].hasHorizontalLine) {
                lines.push(id);
            }
        }
        lines.sort((a, b) => a - b);

        return (
            lines.length === 2 &&
            lines[0] < index - this._minId &&
            index - this._minId <= lines[1]
        );
    }

    isVerticalChannel(index: number): boolean {
        const selected = this.selectVerticalCells(index, false, true);
        selected.push(index);

        const lines: number[] = [];
        for (const id of selected) {
            if (this._cells[id - this._minId].hasVerticalLine) {
                lines.push(id);
            }
        }
        lines.sort((a, b) => a - b);

        return (
            lines.length === 2 &&
            lines[0] < index - this._minId &&
            index - this._minId <= lines[1]
        );
    }

    // ============================================================================
    // Clue Getters
    // ============================================================================

    getVerticalClues(): string[] {
        const clues: string[] = [];
        let clueStr = '';

        for (let i = 0; i < this._cells.length; i++) {
            const cell = this._cells[i];

            if (cell.hasVerticalClue) {
                const errorClass = cell.hasVerticalExpressionError
                    ? ' class="hiba"'
                    : '';
                let clueText =
                    clueStr !== ''
                        ? cell.verticalClue!
                        : capitalize(cell.verticalClue!);

                if (cell.isColored) {
                    clueText = `<b>${clueText}</b>`;
                }

                clueStr += clueStr !== '' ? ' – ' : '';
                clueStr += `<a href="javascript:ugrasMezore(${
                    i + this._minId
                }, true);" desc="${
                    cell.verticalExpression ?? ''
                }"${errorClass}>${clueText}</a>`;
            }

            // End of row (within content area)
            if (
                (i + 1) % this._columns === 0 &&
                (i + 1) / this._columns > this._topMargin &&
                (i + 1) / this._columns <= this._rows - this._topMargin
            ) {
                clues.push(clueStr);
                clueStr = '';
            }
        }

        return clues;
    }

    getHorizontalClues(): string[] {
        const clues: string[] = [];
        let clueStr = '';

        for (let col = 0; col < this._columns; col++) {
            for (let row = 0; row < this._cells.length; row += this._columns) {
                const cell = this._cells[col + row];

                if (cell.hasHorizontalClue) {
                    const errorClass = cell.hasHorizontalExpressionError
                        ? ' class="hiba"'
                        : '';
                    let clueText =
                        clueStr !== ''
                            ? cell.horizontalClue!
                            : capitalize(cell.horizontalClue!);

                    if (this._cells[col].isColored) {
                        clueText = `<b>${clueText}</b>`;
                    }

                    clueStr += clueStr !== '' ? ' – ' : '';
                    clueStr += `<a href="javascript:ugrasMezore(${
                        col + row + this._minId
                    }, false);" desc="${
                        cell.horizontalExpression ?? ''
                    }"${errorClass}>${clueText}</a>`;
                }
            }

            // End of column (within content area)
            if (
                col + 1 > this._leftMargin &&
                col < this._columns - this._leftMargin
            ) {
                clues.push(clueStr);
                clueStr = '';
            }
        }

        return clues;
    }

    getVerticalBackClues(): string[] {
        const clues: string[] = [];
        let clueStr = '';

        for (let i = 0; i < this._cells.length; i++) {
            const cell = this._cells[i];

            if (cell.hasVerticalBackClue) {
                const errorClass = cell.hasVerticalBackExpressionError
                    ? ' class="hiba"'
                    : '';
                const clueText =
                    clueStr !== ''
                        ? cell.verticalBackClue!
                        : capitalize(cell.verticalBackClue!);

                clueStr += clueStr !== '' ? ' – ' : '';
                clueStr += `<a href="javascript:ugrasMezore(${
                    cell.bridgePosition ?? i + this._minId
                }, true);" desc="${
                    cell.verticalBackExpression ?? ''
                }"${errorClass}>${clueText}</a>`;
            }

            // End of row (within content area)
            if (
                (i + 1) % this._columns === 0 &&
                (i + 1) / this._columns > this._topMargin &&
                (i + 1) / this._columns <= this._rows - this._topMargin
            ) {
                clues.push(clueStr);
                clueStr = '';
            }
        }

        return clues;
    }

    getHorizontalBackClues(): string[] {
        const clues: string[] = [];
        let clueStr = '';

        for (let col = 0; col < this._columns; col++) {
            for (let row = 0; row < this._cells.length; row += this._columns) {
                const cell = this._cells[col + row];

                if (cell.hasHorizontalBackClue) {
                    const errorClass = cell.hasHorizontalBackExpressionError
                        ? ' class="hiba"'
                        : '';
                    const clueText =
                        clueStr !== ''
                            ? cell.horizontalBackClue!
                            : capitalize(cell.horizontalBackClue!);

                    clueStr += clueStr !== '' ? ' – ' : '';
                    clueStr += `<a href="javascript:ugrasMezore(${
                        cell.bridgePosition ?? col + row + this._minId
                    }, false);" desc="${
                        cell.horizontalBackExpression ?? ''
                    }"${errorClass}>${clueText}</a>`;
                }
            }

            // End of column (within content area)
            if (
                col + 1 > this._leftMargin &&
                col < this._columns - this._leftMargin
            ) {
                clues.push(clueStr);
                clueStr = '';
            }
        }

        return clues;
    }

    // ============================================================================
    // Validation
    // ============================================================================

    checkClueErrors(): boolean {
        let hasErrors = false;

        // Check horizontal clues
        let i = this._minId;
        while (i < this._maxId) {
            const selected = this.selectHorizontalCells(i);
            selected.push(i);
            selected.sort((a, b) => a - b);

            if (selected.length > 1) {
                const firstCell = this.get(selected[0]);
                if (
                    !firstCell.hasVerticalClue &&
                    !firstCell.hasVerticalBackClue
                ) {
                    hasErrors = true;
                }
            }

            i += selected.length;
        }

        // Check vertical clues
        i = this._minId;
        while (i < this._maxId) {
            const selected = this.selectVerticalCells(i);
            selected.push(i);
            selected.sort((a, b) => a - b);

            if (selected.length > 1) {
                const firstCell = this.get(selected[0]);
                if (
                    !firstCell.hasHorizontalClue &&
                    !firstCell.hasHorizontalBackClue
                ) {
                    hasErrors = true;
                }
            }

            i++;
        }

        return hasErrors;
    }

    checkExpressionErrors(): boolean {
        for (const cell of this._cells) {
            if (cell.hasExpressionError) {
                return true;
            }
        }
        return false;
    }

    /**
     * Initialize expression errors by comparing stored expressions with cell values.
     * Enables error flags when stored expressions don't match calculated expressions.
     * Migrated from legacy initKifejezesHibak function.
     */
    initExpressionErrors(): void {
        // First, clear all expression errors
        for (const cell of this._cells) {
            cell.disableVerticalExpressionError();
            cell.disableHorizontalExpressionError();
            cell.disableVerticalBackExpressionError();
            cell.disableHorizontalBackExpressionError();
        }

        // Check vertical clues (horizontal direction - vmezoSelect)
        let i = this._minId;
        while (i <= this._maxId) {
            const selected = this.selectHorizontalCells(i);
            selected.push(i);
            selected.sort((a, b) => a - b);

            // Build expression from cell values
            let expression = '';
            for (const cellId of selected) {
                const cell = this.get(cellId);
                expression += cell.value ?? '';
            }

            const firstCell = this.get(selected[0]);
            if (
                firstCell.hasVerticalClue &&
                firstCell.verticalExpression !== expression &&
                !this.isHorizontalChannel(i)
            ) {
                firstCell.enableVerticalExpressionError();
            }

            i += selected.length;
        }

        // Check vertical back clues (cell by cell)
        i = this._minId;
        while (i <= this._maxId) {
            const selected = this.selectHorizontalCells(i);
            selected.push(i);
            selected.sort((a, b) => a - b);

            // Build expression from cell values
            let expression = '';
            for (const cellId of selected) {
                const cell = this.get(cellId);
                expression += cell.value ?? '';
            }

            const firstCell = this.get(selected[0]);
            if (
                firstCell.hasVerticalBackClue &&
                firstCell.verticalBackExpression !== expression
            ) {
                firstCell.enableVerticalBackExpressionError();
            }

            i++;
        }

        // Check horizontal clues (vertical direction - fmezoSelect)
        i = this._minId;
        while (i <= this._maxId) {
            const selected = this.selectVerticalCells(i);
            selected.push(i);
            selected.sort((a, b) => a - b);

            // Build expression from cell values
            let expression = '';
            for (const cellId of selected) {
                const cell = this.get(cellId);
                expression += cell.value ?? '';
            }

            const firstCell = this.get(selected[0]);
            if (
                firstCell.hasHorizontalClue &&
                firstCell.horizontalExpression !== expression &&
                !this.isVerticalChannel(i)
            ) {
                firstCell.enableHorizontalExpressionError();
            }

            i++;
        }

        // Check horizontal back clues (cell by cell)
        i = this._minId;
        while (i <= this._maxId) {
            const selected = this.selectVerticalCells(i);
            selected.push(i);
            selected.sort((a, b) => a - b);

            // Build expression from cell values
            let expression = '';
            for (const cellId of selected) {
                const cell = this.get(cellId);
                expression += cell.value ?? '';
            }

            const firstCell = this.get(selected[0]);
            if (
                firstCell.hasHorizontalBackClue &&
                firstCell.horizontalBackExpression !== expression
            ) {
                firstCell.enableHorizontalBackExpressionError();
            }

            i++;
        }
    }

    // ============================================================================
    // Render to HTML
    // ============================================================================

    toHtml(
        showClueMarking = false,
        isHorizontal = true,
        knownMarking = true,
        validCharPattern = '',
    ): string {
        // Row numbers header
        let headerRow = '';
        for (let i = 0; i < this._leftMargin; i++) {
            headerRow += '<div class="számosmező">&nbsp;</div>';
        }
        for (
            let i = this._leftMargin;
            i < this._columns - this._leftMargin;
            i++
        ) {
            headerRow += `<div class="számosmező">${
                i - this._leftMargin + 1
            }</div>`;
        }
        for (let i = this._columns - this._leftMargin; i < this._columns; i++) {
            headerRow += '<div class="számosmező">&nbsp;</div>';
        }

        const cornerStyle =
            this._index % 2 === 0
                ? '<div class="számosmező" style="border-right:1px solid rgb(255, 255, 255);">&nbsp;</div>'
                : '';
        const header = `<div style="clear:both; float:left;">${cornerStyle}${headerRow}</div>`;

        // Column numbers
        let colNumbers = '';
        for (let i = 0; i < this._topMargin; i++) {
            colNumbers +=
                '<div class="számosmező" style="clear:both; float:left;">&nbsp;</div>';
        }
        for (let i = this._topMargin; i < this._rows - this._topMargin; i++) {
            colNumbers += `<div class="számosmező" style="clear:both; float:left;">${
                i - this._topMargin + 1
            }</div>`;
        }
        for (let i = this._rows - this._topMargin; i < this._rows; i++) {
            colNumbers +=
                '<div class="számosmező" style="clear:both; float:left;">&nbsp;</div>';
        }

        let leftCol: string;
        let rightCol: string;

        if (this._index % 2 === 0) {
            leftCol = `<div style="clear:both; float:left;">${colNumbers}</div>`;
            rightCol = '<div>&nbsp;</div>';
        } else {
            rightCol = `<div style="float:left;">${colNumbers}</div>`;
            leftCol =
                '<div class="számosmező" style="border-right:1px solid rgb(255, 255, 255);">&nbsp;</div>';
        }

        // Grid cells - pass rendering parameters from caller
        const cellsHtml = this._cells
            .map((cell) =>
                cell.toHtml(
                    showClueMarking,
                    isHorizontal,
                    knownMarking,
                    validCharPattern,
                ),
            )
            .join('');
        const gridStyle =
            this._index % 2 === 0 ? 'float:left;' : 'float:left; clear:both;';
        const gridDiv = `<div style="${gridStyle}">${cellsHtml}</div>`;

        return `<div id="${this._elementId}" class="hálózat">${header}${leftCol}${gridDiv}${rightCol}</div>`;
    }

    // ============================================================================
    // Serialize to XML
    // ============================================================================

    toXml(): string {
        let xml = `<hálózat vbehúzás="${this._leftMargin}" fbehúzás="${this._topMargin}">`;

        for (let i = 0; i < this._cells.length; i++) {
            if (i % this._columns === 0) {
                xml += '<sor>';
            }
            xml += this._cells[i].toXml();
            if (i % this._columns === this._columns - 1) {
                xml += '</sor>';
            }
        }

        return xml + '</hálózat>';
    }
}
